import { FadeIn, Section, SectionHeading } from "@/components/Section";
import { ArrowRight, TrendingUp, Users, Repeat } from "lucide-react";

export default function CoreInsight() {
  return (
    <Section testId="core-insight-section" className="bg-white">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        <div className="lg:col-span-7">
          <SectionHeading
            eyebrow="Core Insight"
            title={
              <>
                The fastest way to grow revenue isn't new patients —{" "}
                <span className="text-blue-600">it's new programs.</span>
              </>
            }
            intro="Modern clinics are expanding revenue by offering high-demand cash-pay services and products that are already widely purchased by consumers, operationally simple to deploy, and — through the Nodera Vendor Network — sourced at competitive contracted rates negotiated across the platform."
            testId="core-insight-heading"
          />

          <FadeIn delay={0.1}>
            <div className="mt-10 rounded-2xl border border-slate-200 bg-slate-50/60 p-6 lg:p-8">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-blue-600">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                The real bottleneck
              </div>
              <p className="mt-3 font-display text-xl sm:text-2xl font-semibold text-slate-900 leading-snug">
                The challenge is not access — it's execution.
              </p>
              <p className="mt-3 text-slate-600 leading-relaxed">
                Our platform solves this by curating, packaging, and operationalizing the most relevant services into turnkey programs your clinic can deploy immediately.
              </p>
            </div>
          </FadeIn>
        </div>

        <div className="lg:col-span-5 space-y-5">
          {[
            {
              icon: Users,
              k: "Acquisition",
              v: "Slow, expensive, competitive",
              tone: "text-slate-500",
            },
            {
              icon: TrendingUp,
              k: "New programs",
              v: "Fast, margin-rich, repeatable",
              tone: "text-emerald-600",
            },
            {
              icon: Repeat,
              k: "Recurring revenue",
              v: "Subscription-native delivery",
              tone: "text-blue-600",
            },
          ].map((row, i) => (
            <FadeIn key={i} delay={0.08 * i}>
              <div className="group flex items-start gap-4 rounded-xl border border-slate-200 bg-white p-5 hover:border-blue-200 transition-colors">
                <div className="w-10 h-10 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center shrink-0">
                  <row.icon className="w-4 h-4 text-slate-700" strokeWidth={2} />
                </div>
                <div className="flex-1">
                  <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400">
                    {row.k}
                  </div>
                  <div className={`mt-1 font-display text-base font-semibold ${row.tone}`}>{row.v}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors" />
              </div>
            </FadeIn>
          ))}

          <FadeIn delay={0.3}>
            <div className="rounded-xl border border-slate-900 bg-slate-900 text-white p-6">
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-300">
                What we build
              </div>
              <p className="mt-2 font-display text-lg font-semibold leading-snug">
                Infrastructure that turns a curated service catalog into deployable, recurring-revenue programs.
              </p>
            </div>
          </FadeIn>
        </div>
      </div>
    </Section>
  );
}
